import type { RaumplanungAttributeMetadataListEntry, SpatialUnitType } from 'atlas-shared';
import { spatialUnitTypeToRegionalDepth } from 'atlas-shared';
import type { IndicatorFilter } from './IndicatorFilter';

/**
 * Per-leaf administrative-level filter.
 *
 * In the "each leaf node is its own filter" model, every leaf of the
 * "Administrative Ebene" tree node produces one {@link createRegionalDepthFilter}
 * instance that holds exactly one administrative level (e.g. "Gemeinde"). The
 * registry is the single source of truth for whether the instance is
 * active/pinned; `applies` simply checks whether a dataset is available for
 * this level (i.e. its `regionalDepth` contains the matching code).
 */
export class RegionalDepthFilter implements IndicatorFilter<unknown> {

    readonly id: string

    readonly label: string

    /** The single administrative level this leaf filter stands for. */
    readonly level: SpatialUnitType

    /** Optional exclusive group; see {@link IndicatorFilter.exclusiveGroup}. */
    readonly exclusiveGroup?: string

    constructor(level: SpatialUnitType, exclusiveGroup?: string) {
        this.level = level
        this.id = regionalDepthFilterId(level)
        this.label = level
        this.exclusiveGroup = exclusiveGroup
    }

    /**
     * In the per-leaf model the registry is the single source of truth for
     * which filters are active; it only calls `applies` on active filters.
     * Therefore this always checks membership of `this.level` (the inactive
     * gate is managed by the registry, not duplicated here).
     */
    applies(dataset: RaumplanungAttributeMetadataListEntry): boolean {
        const depth = dataset.regionalDepth ?? []
        if (depth.length === 0) return true
        return depth.includes(spatialUnitTypeToRegionalDepth(this.level))
    }

    reset(): void {
        // Nothing to clear — the registry decides activeness via its own lists.
    }

    setConfig(_config: unknown): void {
        // A per-leaf filter has no configurable value besides its fixed level.
    }
}

/**
 * The stable filter id for an administrative-level filter. Single source of
 * truth for the id convention used both when creating a leaf filter and when
 * resolving the filter id associated with a tree leaf node.
 */
export function regionalDepthFilterId(level: SpatialUnitType): string {
    return `regionalDepth:${level}`
}

/**
 * Creates a new per-leaf administrative-level filter for `level`. Each leaf of
 * the "Administrative Ebene" tree node calls this once (and re-registers on
 * tree reloads). `exclusiveGroup` is optional and, when set, makes the filter
 * mutually exclusive with other filters of the same group (see
 * {@link IndicatorFilter.exclusiveGroup}).
 */
export function createRegionalDepthFilter(level: SpatialUnitType, exclusiveGroup?: string): RegionalDepthFilter {
    return new RegionalDepthFilter(level, exclusiveGroup)
}
